/**
 * @file Слой 6 - Глубинное пространство
 * @module membrane/src/layers/Layer6Protected
 * 
 * Шестой слой мембраны - глубинное пространство.
 * Не публичный, доступ только по токену/персональной ссылке.
 * Для тех, кто готов к со-творению.
 */

import { motion } from 'framer-motion';
import { BaseLayerProps } from '../types/membrane';
import { useState, useEffect } from 'react';

/**
 * Компонент Layer6Protected - глубинное пространство
 * 
 * Отображается только для авторизованных пользователей.
 * Содержит закрытые материалы, карты, взаимодействия.
 */
export default function Layer6Protected({ onReturn, state: _state }: BaseLayerProps) {
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [token, setToken] = useState<string>('');
  const [error, setError] = useState<string>('');

  // Проверка токена из URL или localStorage
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlToken = urlParams.get('token');
    const storedToken = localStorage.getItem('membrane_token');

    if (urlToken) {
      validateToken(urlToken);
    } else if (storedToken) {
      validateToken(storedToken);
    }
  }, []);

  /**
   * Валидирует токен доступа
   */
  const validateToken = async (tokenToValidate: string) => {
    try {
      const response = await fetch(`/api/access/${tokenToValidate}`);
      const data = await response.json();

      if (data.hasAccess) {
        setIsAuthorized(true);
        localStorage.setItem('membrane_token', tokenToValidate);
        setError('');
      } else {
        setError('Токен недействителен');
      }
    } catch (err) {
      setError('Ошибка проверки доступа');
      console.error('Token validation error:', err);
    }
  };

  /**
   * Обработка ввода токена
   */
  const handleTokenSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (token) {
      validateToken(token);
    }
  };

  // Если не авторизован, показываем форму входа
  if (!isAuthorized) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 2, ease: 'easeInOut' }}
        className="absolute inset-0 w-full h-full flex items-center justify-center"
      >
        <div className="absolute inset-0 bg-gradient-to-b from-membrane-depth via-membrane-depth/90 to-membrane-depth" />

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 2 }}
          className="relative z-10 max-w-md w-full px-8"
        >
          <h1 className="text-2xl md:text-3xl font-light text-white/90 mb-4 text-center">
            Глубинное пространство
          </h1>
          <p className="text-base text-white/60 font-light text-center mb-8">
            Доступ только для тех, кто готов к со-творению
          </p>

          <form onSubmit={handleTokenSubmit} className="space-y-4">
            <input
              type="text"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="Введите токен доступа"
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-white/40 transition-colors font-light"
            />
            {error && (
              <p className="text-sm text-red-300 font-light text-center">{error}</p>
            )}
            <motion.button
              type="submit"
              whileHover={{ opacity: 0.8 }}
              whileTap={{ scale: 0.98 }}
              className="w-full px-6 py-3 text-white/80 hover:text-white font-light transition-colors border border-white/20 hover:border-white/30 rounded-lg bg-white/10 backdrop-blur-sm"
            >
              Войти
            </motion.button>
          </form>
        </motion.div>

        {onReturn && (
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.5 }}
            whileHover={{ opacity: 0.7 }}
            onClick={onReturn}
            className="absolute top-8 left-8 z-20 text-white/50 hover:text-white/70 transition-opacity"
            aria-label="Вернуться"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 19l-7-7 7-7" />
            </svg>
          </motion.button>
        )}
      </motion.div>
    );
  }

  // Авторизованный контент
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 2, ease: 'easeInOut' }}
      className="absolute inset-0 w-full h-full overflow-y-auto"
    >
      <div className="absolute inset-0 bg-gradient-to-b from-membrane-depth via-membrane-depth/95 to-membrane-depth" />

      <div className="relative z-10 max-w-4xl mx-auto px-8 py-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 2 }}
        >
          <h1 className="text-4xl md:text-5xl font-light text-white/90 mb-8 text-center">
            Глубинное пространство
          </h1>
          <p className="text-xl text-white/70 font-light text-center mb-16 max-w-2xl mx-auto">
            Здесь начинается со-творение. Материалы, карты, диалоги, уровни со-настройки.
          </p>

          <div className="space-y-12">
            <div className="p-8 border border-white/10 rounded-lg bg-white/5 backdrop-blur-sm">
              <h2 className="text-2xl font-light text-white/80 mb-4">Закрытые материалы</h2>
              <p className="text-base text-white/60 font-light leading-relaxed">
                Контент для глубокого взаимодействия будет добавлен здесь.
              </p>
            </div>

            <div className="p-8 border border-white/10 rounded-lg bg-white/5 backdrop-blur-sm">
              <h2 className="text-2xl font-light text-white/80 mb-4">Карты пространств</h2>
              <p className="text-base text-white/60 font-light leading-relaxed">
                Интерактивные карты будущих пространств и их связей.
              </p>
            </div>

            <div className="p-8 border border-white/10 rounded-lg bg-white/5 backdrop-blur-sm">
              <h2 className="text-2xl font-light text-white/80 mb-4">Диалоги</h2>
              <p className="text-base text-white/60 font-light leading-relaxed">
                Пространство для глубокого обмена и со-настройки.
              </p>
            </div>
          </div>
        </motion.div>
      </div>

      {onReturn && (
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.5 }}
          whileHover={{ opacity: 0.7 }}
          onClick={onReturn}
          className="fixed top-8 left-8 z-20 text-white/50 hover:text-white/70 transition-opacity"
          aria-label="Вернуться"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 19l-7-7 7-7" />
          </svg>
        </motion.button>
      )}
    </motion.div>
  );
}

